import { useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CircleNotch } from "@phosphor-icons/react";
import { MatchingProfileEditor } from "@/components/matching/MatchingProfileEditor";
import { GoogleCalendarBlock } from "@/components/therapist/google-calendar-block";
import {
  AvailabilitySection,
  BillingSection,
  ContractSection,
  EmergencySection,
  OverviewSection,
  ProfileSection,
  SECTIONS,
  SectionShell,
  SettingsHeader,
  SettingsNav,
  type EmergencySettingsResponse,
  type GoogleStatusResponse,
  type MatchingProfileResponse,
  type ProfileWithMeta,
  type ServiceSummaryResponse,
  type SettingsSection,
} from "@/components/therapist/settings";
import { apiGet } from "@/lib/api-error";
import { computeTherapistSettingsStatus } from "@/lib/therapist-settings-status";
import type { BookingSettings, DateOverride, TimeBlock } from "@/types";

export const Route = createFileRoute("/dashboard/therapist/settings")({
  validateSearch: (search: Record<string, unknown>): { section?: SettingsSection } => {
    const section = search.section;
    return typeof section === "string" && SECTIONS.some((s) => s.key === section)
      ? { section: section as SettingsSection }
      : {};
  },
  component: TherapistSettingsPage,
});

function TherapistSettingsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/dashboard/therapist/settings" });
  const activeSection = search.section ?? "overview";
  const setSection = (section: SettingsSection) =>
    navigate({ search: section === "overview" ? {} : { section } });

  const profileQuery = useQuery({
    queryKey: ["therapist", "profile", "me"],
    queryFn: () => apiGet<ProfileWithMeta>("/api/therapist/me", "Greška pri učitavanju profila"),
  });
  const profile = profileQuery.data;
  const therapistId = profile?.id;

  const availabilityQuery = useQuery({
    queryKey: ["therapist", "availability"],
    queryFn: () =>
      apiGet<{ schedule: TimeBlock[]; overrides: DateOverride[] }>(
        "/api/therapist/availability",
        "Greška pri učitavanju dostupnosti",
      ),
  });

  const bookingSettingsQuery = useQuery({
    queryKey: ["therapist", "booking-settings"],
    queryFn: () =>
      apiGet<BookingSettings>("/api/therapist/booking-settings", "Greška pri učitavanju booking postavki"),
  });

  const servicesQuery = useQuery({
    queryKey: ["therapist", "services", therapistId],
    enabled: !!therapistId,
    queryFn: () =>
      apiGet<ServiceSummaryResponse>(
        `/api/services?therapistId=${therapistId}&all=1`,
        "Greška pri učitavanju usluga",
      ),
  });

  const matchingQuery = useQuery({
    queryKey: ["therapist", "matching-profile", "me", "summary"],
    queryFn: () =>
      apiGet<MatchingProfileResponse>("/api/me/therapist/matching-profile", "Greška pri učitavanju matching profila"),
  });

  const emergencyQuery = useQuery({
    queryKey: ["emergency-settings"],
    queryFn: () =>
      apiGet<EmergencySettingsResponse>("/api/me/therapist/emergency-settings", "Greška pri učitavanju hitnih postavki"),
  });

  const googleQuery = useQuery({
    queryKey: ["therapist", "google-calendar", "status"],
    queryFn: async () => {
      const res = await fetch("/api/therapist/google/status", { credentials: "include" });
      if (!res.ok) return { connected: false };
      return (await res.json()) as GoogleStatusResponse;
    },
  });

  const activeServices = (servicesQuery.data?.services ?? []).filter((s) => Boolean(s.isActive));
  // Račun za isplatu (banka + broj računa) se traži za oba načina isplate.
  const billingReady = Boolean(
    profile?.isVolunteer ||
      ((profile?.payoutMethod === "invoice"
        ? profile.businessName && profile.businessAddress && profile.businessTaxId
        : profile?.personalAddress && profile.personalTaxId) &&
        profile?.bankName &&
        profile.bankAccount),
  );

  const status = useMemo(() => {
    if (!profile) return null;
    let specCount = 0;
    try {
      const parsed = JSON.parse(profile.specializations || "[]");
      specCount = Array.isArray(parsed) ? parsed.length : 0;
    } catch {
      specCount = 0;
    }

    return computeTherapistSettingsStatus({
      approved: profile.status === "approved",
      deactivated: Boolean(profile.deactivatedAt),
      onboardingCompleted: Boolean(profile.onboardingCompletedAt),
      availabilityCount: availabilityQuery.data?.schedule.length ?? 0,
      activeServiceCount: activeServices.length,
      profile: {
        hasName: Boolean(profile.userName?.trim()),
        hasBio: Boolean(profile.bio?.trim()),
        hasSpecializations: specCount > 0,
        hasPhoto: Boolean(profile.profileImageUrl),
        hasPhone: Boolean(profile.phone?.trim()),
      },
      billingReady,
      matchingReady: matchingQuery.data ? !matchingQuery.data.isEmpty : false,
      emergencyEnabled: emergencyQuery.data?.enabled ?? false,
      googleConnected: googleQuery.data?.connected ?? false,
    });
  }, [
    activeServices.length,
    availabilityQuery.data?.schedule.length,
    billingReady,
    emergencyQuery.data?.enabled,
    googleQuery.data?.connected,
    matchingQuery.data,
    profile,
  ]);

  if (profileQuery.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <CircleNotch className="size-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!profile) {
    return <p className="py-20 text-center text-sm text-muted-foreground">Profil nije pronađen.</p>;
  }

  return (
    <div className="relative mx-auto max-w-6xl pb-14">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -top-10 -z-10 h-72 rounded-[2rem] bg-[radial-gradient(ellipse_at_top_left,_oklch(96%_0.025_84)_0%,_transparent_62%)]"
      />

      <SettingsHeader profile={profile} />

      <div className="grid gap-6 lg:grid-cols-[286px_minmax(0,1fr)]">
        <SettingsNav activeSection={activeSection} onSection={setSection} />

        <main className="min-w-0 space-y-6">
          {activeSection === "overview" && status && (
            <OverviewSection
              status={status}
              profile={profile}
              activeServices={activeServices.length}
              availabilityCount={availabilityQuery.data?.schedule.length ?? 0}
              onSection={setSection}
            />
          )}
          {activeSection === "profile" && <ProfileSection profile={profile} />}
          {activeSection === "availability" && (
            <AvailabilitySection
              availability={availabilityQuery.data}
              bookingSettings={bookingSettingsQuery.data}
              loading={availabilityQuery.isLoading || bookingSettingsQuery.isLoading}
            />
          )}
          {activeSection === "matching" && (
            <SectionShell
              title="Kako sistem preporučuje vaš profil"
              description="Ovi podaci pomažu da klijent dobije relevantnije preporuke, odvojeno od javnog opisa profila."
            >
              <MatchingProfileEditor
                endpoint="/api/me/therapist/matching-profile"
                queryKey={["therapist", "matching-profile", "me"]}
                bare
              />
            </SectionShell>
          )}
          {activeSection === "emergency" && <EmergencySection />}
          {activeSection === "billing" && (
            <>
              <BillingSection profile={profile} />
              <ContractSection />
            </>
          )}
          {/* Privremeno skriveno: Google Calendar integracija
          {activeSection === "integrations" && (
            <SectionShell
              title="Povezani alati"
              description="Trenutno je dostupna Google Calendar integracija za automatsku sinhronizaciju termina."
            >
              <GoogleCalendarBlock />
            </SectionShell>
          )}
          */}
        </main>
      </div>
    </div>
  );
}
